import type {
  Locale,
  Namespace,
  TranslationPath,
  ValidKeysForPath
} from "i18n/constants";
import type { Formats, TranslationValues } from "next-intl";
import { createTranslator, hasLocale } from "next-intl";
import {
  LOCALE_TO_DIRECTORY,
  PATHNAME_TO_TRANSLATION_NAMESPACES
} from "./constants";
import { routing } from "./routing";

export async function loadMessages(locale: Locale, path: TranslationPath) {
  const namespaces = PATHNAME_TO_TRANSLATION_NAMESPACES[path];
  const messages: Record<string, string & Record<"common", string>> = {};

  const directoryLocale = LOCALE_TO_DIRECTORY[locale] || locale;

  messages["common"] = (
    await import(`../locales/${directoryLocale}/common.json`)
  ).default;

  for (const ns of namespaces) {
    messages[ns] = (
      await import(`../locales/${directoryLocale}/${ns}.json`)
    ).default;
    messages[ns]["common"] = (
      await import(`../locales/${directoryLocale}/common.json`)
    ).default;
  }

  return messages;
}

export type TranslateT<P extends TranslationPath> = Awaited<
  ReturnType<typeof getT<P>>
>;

export async function getT<P extends TranslationPath>(
  locale: Locale,
  path: P
): Promise<{
  <TargetKey extends ValidKeysForPath<P>>(
    key: TargetKey,
    values?: TranslationValues,
    formats?: Formats
  ): string;
}> {
  const validLocale = hasLocale(routing.locales, locale) ? locale : "en-US";

  const messages = await loadMessages(validLocale, path);

  const baseT = createTranslator({
    locale,
    messages,
    timeZone: "UTC"
  });

  // Create a callable function and type it
  const t = (<TargetKey extends ValidKeysForPath<P>>(
    key: TargetKey,
    values?: TranslationValues,
    formats?: Formats
    // @ts-expect-error // This is a workaround to allow the function to accept any key
  ) => baseT(key, values, formats)) as {
    <TargetKey extends ValidKeysForPath<P>>(
      key: TargetKey,
      values?: TranslationValues,
      formats?: Formats
    ): string;
  };

  return t;
}

export async function loadMessagesForNamespace(
  locale: Locale,
  namespace: Namespace
) {
  const smartlingLocale = LOCALE_TO_DIRECTORY[locale];
  const messages = (
    await import(`../locales/${smartlingLocale}/${namespace}.json`)
  ).default;
  messages["common"] = (
    await import(`../locales/${smartlingLocale}/common.json`)
  ).default;

  return messages;
}

export async function getTForNamespace(locale: Locale, namespace: Namespace) {
  const validLocale = hasLocale(routing.locales, locale) ? locale : "en-US";
  const messages = await loadMessagesForNamespace(validLocale, namespace);

  return createTranslator({ locale, messages, timeZone: "UTC" });
}
